/**
 * Branding utilities for white-label theming
 * Applies company-specific colors to CSS custom properties
 */

import type { CompanyBranding } from '@/services/companyService';

// Default branding values (matches index.css :root)
const DEFAULT_BRANDING: Required<CompanyBranding> = {
    primaryColor: '#7c3aed', // studio-600
    accentColor: '#06b6d4',  // aquamarine-blue-500
};

/**
 * Convert hex color to HSL values for Tailwind CSS variables
 */
function hexToHSL(hex: string): string {
    // Remove # if present
    hex = hex.replace(/^#/, '');

    // Parse hex values
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
            case r:
                h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
                break;
            case g:
                h = ((b - r) / d + 2) / 6;
                break;
            case b:
                h = ((r - g) / d + 4) / 6;
                break;
        }
    }

    // Return HSL format for CSS variables (without parentheses)
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Generate a color palette from a base color
 */
function generateColorPalette(baseHex: string): Record<string, string> {
    // For now, just return the base color
    // In the future, we could generate lighter/darker variants
    return {
        '500': baseHex,
        '600': baseHex,
        '700': adjustBrightness(baseHex, -15),
        '800': adjustBrightness(baseHex, -30),
    };
}

/**
 * Adjust brightness of a hex color
 */
function adjustBrightness(hex: string, percent: number): string {
    hex = hex.replace(/^#/, '');

    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);

    r = Math.min(255, Math.max(0, r + (r * percent) / 100));
    g = Math.min(255, Math.max(0, g + (g * percent) / 100));
    b = Math.min(255, Math.max(0, b + (b * percent) / 100));

    return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
}

/**
 * Apply company branding to the document's CSS custom properties
 */
export function applyBrandingCSS(branding: CompanyBranding | null | undefined): void {
    const root = document.documentElement;
    const colors = branding || DEFAULT_BRANDING;

    console.log('🎨 BRANDING: Applying colors:', colors);

    // Apply primary color
    if (colors.primaryColor) {
        const primaryHSL = hexToHSL(colors.primaryColor);
        root.style.setProperty('--primary', primaryHSL);
        root.style.setProperty('--ring', primaryHSL);
        root.style.setProperty('--sidebar-primary', primaryHSL);
        root.style.setProperty('--sidebar-ring', primaryHSL);

        // Apply hex values for studio color palette
        const palette = generateColorPalette(colors.primaryColor);
        root.style.setProperty('--studio-500', palette['500']);
        root.style.setProperty('--studio-600', palette['600']);
        root.style.setProperty('--studio-700', palette['700']);
        root.style.setProperty('--studio-800', palette['800']);
    }

    // Apply accent color
    if (colors.accentColor) {
        const accentHSL = hexToHSL(colors.accentColor);
        // Create a lighter version for accent background
        root.style.setProperty('--accent', accentHSL);

        // Apply hex values for aquamarine color palette
        root.style.setProperty('--aquamarine-blue-500', colors.accentColor);
        root.style.setProperty('--aquamarine-blue-600', adjustBrightness(colors.accentColor, -10));
    }
}

/**
 * Reset branding to defaults
 */
export function resetBrandingCSS(): void {
    console.log('🎨 BRANDING: Resetting to defaults');
    applyBrandingCSS(DEFAULT_BRANDING);
}

/**
 * Check if a color is valid hex
 */
export function isValidHexColor(color: string): boolean {
    return /^#[0-9A-Fa-f]{6}$/.test(color);
}
